import React from "react";

import {
  Box,
  ListItemIcon,
  ListItemText,
  MenuItem,
  Select,
  SelectChangeEvent,
  Stack,
  Typography,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";

import { FilterOption } from "@/types";

interface FilterBoxProps {
  label: string;
  value: string;
  options: FilterOption[];
  onChange?: (event: SelectChangeEvent) => void;
  onClick?: (option: string) => void;
  startIcon?: React.ReactNode;
  showSelected?: boolean;
  size?: "medium" | "small";
  width?: number;
  color?: "primary" | "secondary"
  disabled?: boolean;
}

const FilterBox: React.FC<FilterBoxProps> = ({
  label,
  value,
  options,
  onChange,
  onClick,
  startIcon,
  showSelected = false,
  size = "small",
  width = 200,
  color = "primary",
  disabled = false
}) => {
  const theme = useTheme();

  return (
    <Select
      size={size}
      variant="outlined"
      value={value}
      onChange={onChange ?? onChange}
      disabled={disabled}
      displayEmpty
      renderValue={(value: string) => (
        <Box display="flex" flexDirection="row" alignItems="center">
          {startIcon && (
            <Box display="flex" mr={0.5}>
              {startIcon}
            </Box>
          )}
          {showSelected && value !== "" ? (
            <Stack direction="row" spacing={1} alignItems="center">
              {!startIcon && options.find((option) => option.name === value)?.icon}
              <Typography color="textPrimary">
                {options.find((option) => option.name === value)?.label}
              </Typography>
            </Stack>
          ) : (
            <Typography color="textSecondary">{label}</Typography>
          )}
        </Box>
      )}
      sx={{
        minWidth: {width},
        "& > fieldset": {
          borderColor: value !== "" ? theme.palette[color].main : "",
        },
      }}
    >
      {options.map((option) => (
        <MenuItem
          key={option.name}
          value={option.name}
          onClick={() => (onClick ? onClick(option?.name || "") : {})}
        >
          {option.icon &&
            <ListItemIcon>
              {option.icon}
            </ListItemIcon>
          }
          <ListItemText>
            {option.label}
          </ListItemText>
        </MenuItem>
      ))}
    </Select>
  );
};

export default FilterBox;
